import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Small } from 'foundations-components/transitional/components/Small';
import { useCallback, useMemo } from 'react';
import { PlanCompatibilityMessage } from '../../compatibility/PlanCompatibilityMessage';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import { useFeatureCompatibilities } from '../../hooks/useFeatureCompatibilities';
import { PermissionsMessage } from '../../permissions/PermissionsMessage';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ProductRequirementsCard({
  installType,
  features,
  integrationFeatures,
  builtByHubSpot,
  neededScopeGroups,
  onCompatibilityModalOpen,
  pricingPlans,
  tiers
}) {
  const {
    name,
    productType,
    published,
    appContext,
    trackInteraction
  } = useDetailsPageContext();
  const {
    result: featureCompatibilitiesData,
    loading: featureCompatibilitiesLoading,
    error: featureCompatibilitiesError
  } = useFeatureCompatibilities({
    appContext,
    features,
    productType
  });
  const scrollToPricing = () => {
    const tab = document.querySelector(`#details-page-pricing`);
    if (tab) {
      tab.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const compatibilityContext = appContext === 'app' ? 'app' : 'public';
  const isPreview = !published;
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product requirements card'
    }, eventProps));
  }, [trackInteraction]);
  const compatibilityType = useMemo(() => {
    const compatibilities = Object.values(featureCompatibilitiesData);
    if (compatibilities.length === 0) {
      return 'COMPATIBLE_ALL'; // Default to compatible when no features exist
    }
    const allCompatible = compatibilities.every(compatibility => compatibility.satisfied);
    const noneCompatible = compatibilities.every(compatibility => !compatibility.satisfied);
    if (allCompatible) {
      return 'COMPATIBLE_ALL';
    } else if (noneCompatible) {
      return 'COMPATIBLE_NONE';
    } else {
      return 'COMPATIBLE_SOME';
    }
  }, [featureCompatibilitiesData]);
  const renderedPricingMessage = useMemo(() => {
    if (productType === 'APP' && pricingPlans) {
      return /*#__PURE__*/_jsx(Link, {
        onClick: () => {
          handleTrackInteraction({
            action: 'click pricing link'
          });
          scrollToPricing();
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: `detailsPage.requirementsCard.pricing.text.${productType}`,
            options: {
              count: pricingPlans.length,
              plans: /*#__PURE__*/_jsx(FormattedList, {
                exclusive: true,
                children: pricingPlans.map((pricing, index) => /*#__PURE__*/_jsx("span", {
                  children: pricing.pricingName
                }, `pricing-${index}`))
              })
            }
          })
        })
      });
    } else if (productType === 'AGENT') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.requirementsCard.pricing.text.${productType}`,
        options: {
          count: 2
        }
      });
    }
    return null;
  }, [handleTrackInteraction, pricingPlans, productType]);
  if (productType === 'AGENT') return null;
  let renderedPlanCompatibilityMessage;
  if (featureCompatibilitiesLoading) {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "small"
    });
  } else if (featureCompatibilitiesError) {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlock: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.requirementsCard.compatibilityError"
      })
    });
  } else {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(PlanCompatibilityMessage, {
      appContext: compatibilityContext,
      compatibilityType: compatibilityType,
      hasFeatureLevelScopeInfo: !!(features !== null && features !== void 0 && features.length) && !(integrationFeatures !== null && integrationFeatures !== void 0 && integrationFeatures.length),
      helpText: compatibilityType === 'COMPATIBLE_SOME' ? /*#__PURE__*/_jsx(HelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.disclaimer"
        })
      }) : undefined,
      onCompatibilityModalOpen: () => {
        handleTrackInteraction({
          action: 'open compatibility modal'
        });
        onCompatibilityModalOpen();
      },
      onTrackInteraction: handleTrackInteraction,
      productType: productType,
      tiers: tiers
    });
  }
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.requirementsCard.title"
    }),
    children: /*#__PURE__*/_jsxs(DescriptionList, {
      children: [!isPreview && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.hubspotPlan"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: renderedPlanCompatibilityMessage
        })]
      }), neededScopeGroups && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.permissions.title"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(PermissionsMessage, {
            installType: installType,
            builtByHubSpot: builtByHubSpot,
            neededScopeGroups: neededScopeGroups,
            onTrackInteraction: handleTrackInteraction
          })
        })]
      }), pricingPlans && pricingPlans.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.requirementsCard.pricing.title.${productType}`,
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: renderedPricingMessage
        })]
      })]
    })
  });
}